// frontend/src/components/games/RacingGame.jsx
// FIXED: Proper score and position synchronization

import React, { useState, useEffect } from 'react';
import { io } from 'socket.io-client';
import api from '../../utils/api';

const RacingGame = ({ session, onLeave, onRefresh }) => {
    const [socket, setSocket] = useState(null);
    const [gameState, setGameState] = useState(null);
    const [currentUser, setCurrentUser] = useState(null);
    const [connectionStatus, setConnectionStatus] = useState('connecting');
    const [error, setError] = useState(null);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [roundWinner, setRoundWinner] = useState(null);
    const [showRoundResult, setShowRoundResult] = useState(false);
    const [gameWinner, setGameWinner] = useState(null);
    const [showFinalResult, setShowFinalResult] = useState(false);

    useEffect(() => {
        const fetchUser = async () => {
            try {
                const res = await api.get('/auth/me');
                const userData = res.data.data || res.data.user || res.data;
                if (!userData) throw new Error('User data not found');
                setCurrentUser(userData);
                console.log('✅ Current user loaded:', userData.name || userData.username);
            } catch (error) {
                console.error('Fetch user error:', error);
                setError('Failed to load user data. Please refresh the page.');
            }
        };
        fetchUser();
    }, []);

    useEffect(() => {
        if (!currentUser) return;

        const socketURL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:5000';
        const newSocket = io(socketURL, {
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionDelay: 1000,
            reconnectionAttempts: 5,
            timeout: 10000
        });

        newSocket.on('connect', () => {
            console.log('✅ Socket connected');
            setConnectionStatus('connected');
            setError(null);

            newSocket.emit('join-game', {
                sessionCode: session.sessionCode,
                userId: currentUser._id || currentUser.id,
                userName: currentUser.name || currentUser.username
            });
        });

        newSocket.on('connect_error', (err) => {
            console.error('❌ Socket error:', err);
            setConnectionStatus('error');
            setError('Connection failed. Using fallback mode.');
            loadSessionFallback();
        });

        newSocket.on('game-state', (state) => {
            console.log('📦 Received game-state:', state);
            setGameState(state);
            setConnectionStatus('ready');
        });

        newSocket.on('game-started', (state) => {
            console.log('🎮 Game started:', state);
            setGameState(state);
            setSelectedAnswer(null);
            setShowRoundResult(false);
            setRoundWinner(null);
        });

        newSocket.on('player-joined', ({ userName, players }) => {
            console.log(`👋 ${userName} joined`);
            setGameState(prev => prev ? { ...prev, players } : prev);
        });

        newSocket.on('player-answered', ({ userId, userName, answer, isCorrect, gameState: newState }) => {
            console.log(`💬 ${userName} answered:`, isCorrect ? 'CORRECT ✅' : 'WRONG ❌');
            console.log('📊 Updated scores:', newState.players.map(p => `${p.userName}: ${p.score}`));
            console.log('🏎️ Updated positions:', newState.gameData.positions);

            // CRITICAL: Update the entire gameState with new scores and positions
            setGameState(newState);

            if (isCorrect) {
                const finisher = newState.players.find(p =>
                    (newState.gameData.positions?.[p.userId] || 0) >= 100
                );

                if (finisher) {
                    console.log(`🏆 ${finisher.userName} finished the race!`);
                    setRoundWinner({
                        userId: finisher.userId,
                        userName: finisher.userName,
                        position: 1,
                        score: finisher.score
                    });
                    setShowRoundResult(true);

                    setTimeout(() => {
                        setShowRoundResult(false);
                        setRoundWinner(null);
                        setSelectedAnswer(null);
                    }, 3000);
                }
            }
        });

        newSocket.on('timer-update', (timeLeft) => {
            setGameState(prev => prev ? ({
                ...prev,
                gameData: { ...prev.gameData, timeLeft }
            }) : prev);
        });

        newSocket.on('time-up', (state) => {
            console.log('⏰ Time is up!');
            setGameState(state);
            setSelectedAnswer(null);
        });

        newSocket.on('next-round', (state) => {
            console.log('➡️ Next round:', state);
            setGameState(state);
            setSelectedAnswer(null);
        });

        newSocket.on('game-finished', (data) => {
            console.log('🏆 Game finished:', data);
            setGameState(data.gameState);
            setGameWinner(data.winner);
            setShowFinalResult(true);
        });

        setSocket(newSocket);

        return () => {
            if (currentUser) {
                newSocket.emit('leave-game', {
                    sessionCode: session.sessionCode,
                    userId: currentUser._id || currentUser.id
                });
            }
            newSocket.disconnect();
        };
    }, [currentUser, session.sessionCode]);

    const loadSessionFallback = async () => {
        try {
            const response = await api.get(`/funzone/session/${session.sessionCode}`);
            const sessionData = response.data.data || response.data;

            const fallbackState = {
                sessionCode: sessionData.sessionCode,
                gameType: sessionData.gameType,
                status: sessionData.status,
                players: sessionData.players.map(p => ({
                    userId: p.user._id || p.user.id,
                    userName: p.user.name || p.user.username,
                    nickname: p.nickname || p.user.name || p.user.username,
                    score: p.score || 0,
                    isActive: true
                })),
                gameData: sessionData.gameData || {
                    currentQuestion: null,
                    timeLeft: 15,
                    positions: {}
                },
                currentRound: sessionData.currentRound || 0,
                totalRounds: sessionData.totalRounds || 10
            };

            setGameState(fallbackState);
            setConnectionStatus('fallback');
        } catch (error) {
            console.error('Fallback load error:', error);
            setError('Failed to load game session');
        }
    };

    useEffect(() => {
        if (!socket || !gameState || gameState.status !== 'playing') return;
        if (showRoundResult) return;

        const timer = setInterval(() => {
            socket.emit('timer-tick', { sessionCode: session.sessionCode });
        }, 1000);

        return () => clearInterval(timer);
    }, [socket, gameState, session.sessionCode, showRoundResult]);

    const handleStartGame = async () => {
        if (socket && connectionStatus === 'ready') {
            socket.emit('start-game', { sessionCode: session.sessionCode });
        } else {
            try {
                await api.post(`/funzone/session/${session.sessionCode}/start`);
                await loadSessionFallback();
                alert('Game started!');
            } catch (error) {
                alert(error.response?.data?.message || 'Failed to start game');
            }
        }
    };

    const handleFinishGame = async () => {
        try {
            await api.post(`/funzone/session/${session.sessionCode}/finish`);

            const sortedPlayers = [...gameState.players].sort((a, b) => b.score - a.score);
            const winner = sortedPlayers[0];

            setGameWinner(winner);
            setShowFinalResult(true);
        } catch (error) {
            alert('Failed to finish game');
        }
    };

    const handleAnswer = async (answerIndex) => {
        if (selectedAnswer !== null) return;

        console.log(`Submitting answer: ${answerIndex}`);
        setSelectedAnswer(answerIndex);

        if (socket && connectionStatus === 'ready') {
            socket.emit('game-action', {
                sessionCode: session.sessionCode,
                action: 'answer',
                data: { answer: answerIndex }
            });
        }
    };

    const getPosition = (userId) => {
        if (!gameState || !gameState.gameData || !gameState.gameData.positions) return 0;
        return gameState.gameData.positions[userId] || 0;
    };

    const getCarColor = (index) => {
        const colors = [
            'from-red-500 to-red-700',
            'from-blue-500 to-blue-700',
            'from-green-500 to-green-700',
            'from-yellow-500 to-yellow-700',
            'from-purple-500 to-purple-700',
            'from-pink-500 to-pink-700',
            'from-indigo-500 to-indigo-700',
            'from-orange-500 to-orange-700'
        ];
        return colors[index % colors.length];
    };

    const amIWinner = () => {
        if (!roundWinner || !currentUser) return false;
        const currentUserId = currentUser._id || currentUser.id;
        return roundWinner.userId === currentUserId;
    };

    const amIGameWinner = () => {
        if (!gameWinner || !currentUser) return false;
        const currentUserId = currentUser._id || currentUser.id;
        return gameWinner.userId === currentUserId;
    };

    if (!currentUser || !gameState) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-900 via-cyan-900 to-teal-900 text-white flex items-center justify-center">
                <div className="text-center max-w-md">
                    {error ? (
                        <>
                            <div className="text-6xl mb-4">⚠️</div>
                            <p className="text-2xl font-bold mb-2">Connection Issue</p>
                            <p className="text-gray-300 mb-6">{error}</p>
                            <button
                                onClick={() => window.location.reload()}
                                className="px-6 py-3 bg-blue-600 rounded-lg hover:bg-blue-700 transition"
                            >
                                Refresh Page
                            </button>
                        </>
                    ) : (
                        <>
                            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-white mx-auto mb-4"></div>
                            <p className="text-xl">Loading race...</p>
                        </>
                    )}
                </div>
            </div>
        );
    }

    // Round Result Modal
    if (showRoundResult && roundWinner) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-900 via-cyan-900 to-teal-900 text-white flex items-center justify-center p-4">
                <div className="text-center max-w-2xl animate-bounce">
                    {amIWinner() ? (
                        <>
                            <div className="text-9xl mb-6">🏆</div>
                            <h1 className="text-7xl font-bold mb-4 text-yellow-400 drop-shadow-lg">WINNER!</h1>
                            <p className="text-4xl mb-6">You finished <span className="text-green-400 font-bold">1st place!</span></p>
                            <p className="text-2xl text-gray-200">Final Score: {roundWinner.score} points</p>
                        </>
                    ) : (
                        <>
                            <div className="text-9xl mb-6">🏁</div>
                            <h1 className="text-7xl font-bold mb-4 text-orange-400 drop-shadow-lg">RACE OVER!</h1>
                            <p className="text-4xl mb-6"><span className="text-yellow-400 font-bold">{roundWinner.userName}</span> finished first!</p>
                            <p className="text-2xl text-gray-200">Better luck next time!</p>
                        </>
                    )}
                    <p className="text-sm text-gray-400 mt-8 animate-pulse">Next race starting...</p>
                </div>
            </div>
        );
    }

    // Final Game Result Modal
    if (showFinalResult && gameWinner) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-pink-900 text-white flex items-center justify-center p-4">
                <div className="text-center max-w-4xl">
                    <div className="text-9xl mb-6">🏆</div>
                    <h1 className="text-8xl font-bold mb-6 drop-shadow-2xl">RACE COMPLETE!</h1>

                    {amIGameWinner() ? (
                        <>
                            <div className="text-9xl mb-6 animate-bounce">🎉</div>
                            <h2 className="text-6xl font-bold mb-4 text-yellow-400 drop-shadow-lg">CHAMPION RACER!</h2>
                            <p className="text-4xl mb-8">Total Score: <span className="text-green-400 font-bold">{gameWinner.score} points</span></p>
                        </>
                    ) : (
                        <>
                            <h2 className="text-5xl font-bold mb-4"><span className="text-yellow-400">{gameWinner.userName}</span> Wins!</h2>
                            <p className="text-3xl mb-8">Total Score: <span className="text-green-400 font-bold">{gameWinner.score} points</span></p>
                        </>
                    )}

                    <div className="bg-gray-800 bg-opacity-70 backdrop-blur-sm rounded-3xl p-8 mb-8 shadow-2xl">
                        <h3 className="text-4xl font-bold mb-6">Final Standings</h3>
                        <div className="space-y-4">
                            {[...gameState.players]
                                .sort((a, b) => b.score - a.score)
                                .map((player, idx) => (
                                    <div key={idx} className="flex items-center justify-between bg-gray-700 bg-opacity-70 rounded-xl p-6 hover:bg-opacity-90 transition">
                                        <div className="flex items-center gap-4">
                                            <span className="text-5xl">
                                                {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `#${idx + 1}`}
                                            </span>
                                            <span className="text-3xl font-bold">{player.userName}</span>
                                        </div>
                                        <span className="text-4xl font-bold text-yellow-400">{player.score} pts</span>
                                    </div>
                                ))}
                        </div>
                    </div>

                    <button
                        onClick={onLeave}
                        className="px-12 py-6 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-2xl font-bold text-2xl hover:from-blue-700 hover:to-purple-700 shadow-2xl transform hover:scale-105 transition"
                    >
                        Back to Lobby
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-900 via-cyan-900 to-teal-900 text-white p-2 sm:p-4 md:p-6">
            {/* Header */}
            <div className="max-w-7xl mx-auto mb-4">
                <div className="flex flex-col sm:flex-row justify-between items-center bg-gray-800 bg-opacity-80 backdrop-blur-sm rounded-2xl p-4 gap-4 shadow-xl">
                    <div className="text-center sm:text-left">
                        <h1 className="text-3xl sm:text-4xl font-bold flex items-center gap-2 justify-center sm:justify-start">
                            🏎️ Team Racing
                            {connectionStatus === 'ready' && (
                                <span className="px-2 py-1 bg-green-500 text-xs rounded-full animate-pulse">● Live</span>
                            )}
                            {connectionStatus === 'fallback' && (
                                <span className="px-2 py-1 bg-yellow-500 text-xs rounded-full">Offline</span>
                            )}
                        </h1>
                        <p className="text-sm text-gray-300">Session: #{session.sessionCode}</p>
                        {gameState.status === 'playing' && (
                            <p className="text-sm text-yellow-400 font-semibold">
                                Question {gameState.currentRound}/{gameState.totalRounds}
                            </p>
                        )}
                    </div>
                    <div className="flex items-center gap-3">
                        {gameState.status === 'playing' && (
                            <div className="text-center bg-gradient-to-br from-orange-500 to-red-500 px-6 py-3 rounded-xl shadow-lg">
                                <div className="text-4xl font-bold">{gameState.gameData?.timeLeft || 0}s</div>
                                <div className="text-xs">Time Left</div>
                            </div>
                        )}
                        {gameState.status === 'playing' && (
                            <button
                                onClick={handleFinishGame}
                                className="px-4 py-2 bg-purple-600 rounded-xl hover:bg-purple-700 transition shadow-lg"
                            >
                                End Race
                            </button>
                        )}
                        <button
                            onClick={onLeave}
                            className="px-4 py-2 bg-red-600 rounded-xl hover:bg-red-700 transition shadow-lg"
                        >
                            Leave
                        </button>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-4 gap-4">
                {/* Players & Race Track Panel */}
                <div className="lg:col-span-1 bg-gray-800 bg-opacity-80 backdrop-blur-sm rounded-2xl p-4 shadow-xl">
                    <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                        <span>👥</span> Racers ({gameState.players.filter(p => p.isActive).length})
                    </h3>
                    <div className="space-y-3">
                        {gameState.players.map((player, idx) => {
                            const position = getPosition(player.userId);

                            return (
                                <div
                                    key={idx}
                                    className="bg-gray-700 bg-opacity-70 rounded-xl p-3 transition transform hover:scale-105"
                                >
                                    <div className="flex justify-between items-center mb-2">
                                        <div>
                                            <span className="font-bold text-sm">{player.userName}</span>
                                            {!player.isActive && (
                                                <div className="text-xs text-gray-400">(Offline)</div>
                                            )}
                                        </div>
                                        <span className="text-yellow-400 font-bold text-lg">{player.score}</span>
                                    </div>

                                    {/* Mini progress bar */}
                                    <div className="relative h-6 bg-gray-900 rounded-full overflow-hidden">
                                        <div className="absolute right-0 top-0 bottom-0 w-1 bg-white opacity-50"></div>
                                        <div
                                            className={`h-full bg-gradient-to-r ${getCarColor(idx)} transition-all duration-500 flex items-center justify-end pr-1`}
                                            style={{ width: `${position}%` }}
                                        >
                                            <span className="text-xs">🏎️</span>
                                        </div>
                                    </div>
                                    <div className="text-right text-xs text-gray-400 mt-1">
                                        {position.toFixed(0)}%
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {gameState.status === 'waiting' && (
                        <button
                            onClick={handleStartGame}
                            className="w-full mt-6 bg-gradient-to-r from-green-500 to-emerald-500 text-white py-4 px-6 rounded-xl font-bold text-lg hover:from-green-600 hover:to-emerald-600 transition shadow-xl transform hover:scale-105"
                        >
                            🏁 Start Race
                        </button>
                    )}
                </div>

                {/* Race Track & Question Panel */}
                <div className="lg:col-span-3 space-y-4">
                    {/* Race Track */}
                    <div className="bg-gray-800 bg-opacity-80 backdrop-blur-sm rounded-2xl p-6 shadow-xl">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-2xl font-bold flex items-center gap-2">
                                🏁 Race Track
                            </h3>
                            {gameState.status === 'playing' && (
                                <div className="text-sm text-gray-400">
                                    First to 100% wins!
                                </div>
                            )}
                        </div>

                        <div className="space-y-4">
                            {gameState.players.map((player, idx) => {
                                const position = getPosition(player.userId);

                                return (
                                    <div key={idx}>
                                        <div className="flex items-center gap-3 mb-2">
                                            <span className="text-sm font-semibold w-32 truncate">
                                                {player.userName}
                                            </span>
                                            <span className="text-yellow-400 font-bold text-sm">{player.score} pts</span>
                                        </div>

                                        {/* Track */}
                                        <div className="relative h-16 bg-gradient-to-r from-gray-700 via-gray-600 to-gray-700 rounded-full overflow-hidden shadow-inner">
                                            {/* Grid lines */}
                                            <div className="absolute inset-0 flex">
                                                {[...Array(10)].map((_, i) => (
                                                    <div key={i} className="flex-1 border-r border-gray-500 opacity-20"></div>
                                                ))}
                                            </div>

                                            {/* Finish line */}
                                            <div className="absolute right-0 top-0 bottom-0 w-2 bg-gradient-to-b from-white via-gray-300 to-white opacity-70"></div>

                                            {/* Car */}
                                            <div
                                                className={`absolute top-1 bottom-1 w-14 bg-gradient-to-r ${getCarColor(idx)} rounded-full flex items-center justify-center text-3xl shadow-lg transition-all duration-500 ${position >= 100 ? 'animate-bounce' : ''
                                                    }`}
                                                style={{ left: `${Math.min(position, 100)}%`, transform: 'translateX(-50%)' }}
                                            >
                                                🏎️
                                            </div>
                                        </div>

                                        {/* Progress percentage */}
                                        <div className="flex justify-between items-center mt-1 text-xs">
                                            <span className="text-gray-400">0%</span>
                                            <span className="text-gray-300 font-bold">{position.toFixed(0)}%</span>
                                            <span className="text-gray-400">100%</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Question Panel */}
                    {gameState.status === 'playing' && gameState.gameData?.currentQuestion ? (
                        <div className="bg-gray-800 bg-opacity-80 backdrop-blur-sm rounded-2xl p-8 shadow-xl">
                            <div className="mb-6 text-center">
                                <div className="inline-block px-4 py-2 bg-blue-600 rounded-full text-sm font-bold mb-4">
                                    Question {gameState.currentRound}
                                </div>
                                <h3 className="text-3xl font-bold mb-2">{gameState.gameData.currentQuestion.question}</h3>
                                <p className="text-sm text-gray-400">Answer correctly to boost your speed!</p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {gameState.gameData.currentQuestion.options.map((option, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => handleAnswer(idx)}
                                        disabled={selectedAnswer !== null}
                                        className={`p-6 rounded-xl text-lg font-bold transition transform hover:scale-105 ${selectedAnswer === null
                                                ? 'bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 shadow-lg'
                                                : selectedAnswer === idx
                                                    ? idx === gameState.gameData.currentQuestion.correct
                                                        ? 'bg-gradient-to-r from-green-600 to-emerald-600 ring-4 ring-green-400 shadow-xl'
                                                        : 'bg-gradient-to-r from-red-600 to-rose-600 ring-4 ring-red-400 shadow-xl'
                                                    : idx === gameState.gameData.currentQuestion.correct
                                                        ? 'bg-gradient-to-r from-green-600 to-emerald-600 ring-4 ring-green-400 shadow-xl'
                                                        : 'bg-gray-600 opacity-50'
                                            } disabled:cursor-not-allowed`}
                                    >
                                        {option}
                                        {selectedAnswer !== null && idx === gameState.gameData.currentQuestion.correct && ' ✓'}
                                        {selectedAnswer === idx && idx !== gameState.gameData.currentQuestion.correct && ' ✗'}
                                    </button>
                                ))}
                            </div>

                            {selectedAnswer !== null && (
                                <div className="mt-6 text-center">
                                    <p className={`text-2xl font-bold ${selectedAnswer === gameState.gameData.currentQuestion.correct
                                            ? 'text-green-400'
                                            : 'text-red-400'
                                        }`}>
                                        {selectedAnswer === gameState.gameData.currentQuestion.correct
                                            ? '🎉 Correct! Your car speeds up!'
                                            : '❌ Wrong answer! No boost.'}
                                    </p>
                                </div>
                            )}

                            {/* Scoring Info */}
                            <div className="mt-6 bg-blue-900 bg-opacity-50 rounded-xl p-4">
                                <h4 className="font-bold mb-2 text-center">💡 How to Win:</h4>
                                <div className="text-sm text-gray-300 space-y-1">
                                    <div className="flex items-center justify-between">
                                        <span>✓ Correct answer:</span>
                                        <span className="text-yellow-400 font-bold">+50 points & +20% progress</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span>✓ Reach 100% first:</span>
                                        <span className="text-green-400 font-bold">+300 bonus points!</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : gameState.status === 'waiting' ? (
                        <div className="bg-gray-800 bg-opacity-80 backdrop-blur-sm rounded-2xl p-12 text-center shadow-xl">
                            <div className="text-9xl mb-6">🏁</div>
                            <h2 className="text-5xl font-bold mb-4">Ready to Race?</h2>
                            <p className="text-xl text-gray-300 mb-8">
                                Answer questions correctly to boost your car's speed!
                            </p>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                                <div className="bg-gradient-to-br from-blue-600 to-cyan-600 rounded-xl p-6 shadow-lg">
                                    <div className="text-5xl mb-3">🎯</div>
                                    <div className="font-bold text-lg">Answer Questions</div>
                                    <div className="text-sm text-gray-200 mt-2">Get them right to move forward</div>
                                </div>
                                <div className="bg-gradient-to-br from-green-600 to-emerald-600 rounded-xl p-6 shadow-lg">
                                    <div className="text-5xl mb-3">🏎️</div>
                                    <div className="font-bold text-lg">Race Ahead</div>
                                    <div className="text-sm text-gray-200 mt-2">Speed boost for each correct answer</div>
                                </div>
                                <div className="bg-gradient-to-br from-yellow-600 to-orange-600 rounded-xl p-6 shadow-lg">
                                    <div className="text-5xl mb-3">🏆</div>
                                    <div className="font-bold text-lg">Win Big</div>
                                    <div className="text-sm text-gray-200 mt-2">Finish first for bonus points!</div>
                                </div>
                            </div>

                            <div className="bg-blue-900 bg-opacity-50 rounded-xl p-6">
                                <h3 className="text-2xl font-bold mb-3">Race Rules:</h3>
                                <div className="text-left space-y-2 text-gray-300 max-w-md mx-auto">
                                    <div className="flex items-start gap-2">
                                        <span className="text-green-400">✓</span>
                                        <span>Correct answer = +50 points + 20% progress</span>
                                    </div>
                                    <div className="flex items-start gap-2">
                                        <span className="text-green-400">✓</span>
                                        <span>First to 100% wins the race</span>
                                    </div>
                                    <div className="flex items-start gap-2">
                                        <span className="text-green-400">✓</span>
                                        <span>Winner gets +300 bonus points</span>
                                    </div>
                                    <div className="flex items-start gap-2">
                                        <span className="text-green-400">✓</span>
                                        <span>15 seconds per question</span>
                                    </div>
                                    <div className="flex items-start gap-2">
                                        <span className="text-green-400">✓</span>
                                        <span>Highest total score wins overall!</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default RacingGame;